import { Rarity } from '@sorare/core/src/__generated__/globalTypes';

import { isArenaMode, Mode, type ArenaMode } from '#src/lib/gameMode.ts';

type ProModeState = {
  lastVisitedBoardTitle?: string;
};

type ArenaModeState = {
  lastVisitedSo5Competition?: string;
  lastVisitedArenaRarity?: Rarity;
};

type SportBoardView = {
  lastVisitedMode?: Mode;
  modes?: {
    PRO?: ProModeState;
    ARENA_IN_SEASON?: ArenaModeState;
    ARENA_CLASSIC?: ArenaModeState;
  };
};

type LastVisitedBoardView = {
  [sport: string]: SportBoardView;
};

type FlatLastVisitedView = {
  lastVisitedMode?: Mode;
  lastVisitedBoardTitle?: string;
  lastVisitedSo5Competition?: string;
  lastVisitedArenaRarity?: Rarity;
};

export type BoardViewUpdate = {
  lastVisitedMode?: Mode;
  lastVisitedBoardTitle?: string;
  lastVisitedSo5Competition?: string;
  lastVisitedArenaRarity?: Rarity;
};

const MODES = Object.values(Mode);
const RARITIES = Object.values(Rarity);

const isMode = (value: unknown): value is Mode =>
  typeof value === 'string' && (MODES as string[]).includes(value);

const isRarity = (value: unknown): value is Rarity =>
  typeof value === 'string' && (RARITIES as string[]).includes(value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const normalizeProMode = (raw: unknown): ProModeState | undefined => {
  if (!isRecord(raw)) return undefined;
  const lastVisitedBoardTitle =
    typeof raw.lastVisitedBoardTitle === 'string'
      ? raw.lastVisitedBoardTitle
      : undefined;
  if (!lastVisitedBoardTitle) return undefined;
  return { lastVisitedBoardTitle };
};

const normalizeArenaMode = (raw: unknown): ArenaModeState | undefined => {
  if (!isRecord(raw)) return undefined;
  const lastVisitedSo5Competition =
    typeof raw.lastVisitedSo5Competition === 'string'
      ? raw.lastVisitedSo5Competition
      : undefined;
  const lastVisitedArenaRarity = isRarity(raw.lastVisitedArenaRarity)
    ? raw.lastVisitedArenaRarity
    : undefined;
  if (!lastVisitedSo5Competition && !lastVisitedArenaRarity) return undefined;
  return { lastVisitedSo5Competition, lastVisitedArenaRarity };
};

const normalizeSportBoardView = (raw: unknown): SportBoardView | undefined => {
  if (!isRecord(raw)) return undefined;

  const lastVisitedMode = isMode(raw.lastVisitedMode)
    ? raw.lastVisitedMode
    : undefined;

  let modes: SportBoardView['modes'];
  if (isRecord(raw.modes)) {
    const pro = normalizeProMode(raw.modes.PRO);
    const inSeason = normalizeArenaMode(raw.modes.ARENA_IN_SEASON);
    const classic = normalizeArenaMode(raw.modes.ARENA_CLASSIC);
    if (pro || inSeason || classic) {
      modes = {
        ...(pro ? { PRO: pro } : {}),
        ...(inSeason ? { ARENA_IN_SEASON: inSeason } : {}),
        ...(classic ? { ARENA_CLASSIC: classic } : {}),
      };
    }
  }

  if (!lastVisitedMode && !modes) return undefined;
  return { lastVisitedMode, modes };
};

export const normalizeLastVisitedBoardView = (
  raw: unknown
): LastVisitedBoardView | undefined => {
  if (!isRecord(raw)) return undefined;

  const normalized: LastVisitedBoardView = {};
  for (const [sportKey, value] of Object.entries(raw)) {
    const sportView = normalizeSportBoardView(value);
    if (sportView) {
      normalized[sportKey] = sportView;
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

export const toFlatView = (
  sportView: SportBoardView | undefined
): FlatLastVisitedView | undefined => {
  if (!sportView) return undefined;

  const pro = sportView.modes?.PRO;
  const arena = isArenaMode(sportView.lastVisitedMode)
    ? sportView.modes?.[sportView.lastVisitedMode]
    : undefined;

  return {
    lastVisitedMode: sportView.lastVisitedMode,
    lastVisitedBoardTitle: pro?.lastVisitedBoardTitle,
    lastVisitedSo5Competition: arena?.lastVisitedSo5Competition,
    lastVisitedArenaRarity: arena?.lastVisitedArenaRarity,
  };
};

const resolveArenaWriteMode = (
  partial: BoardViewUpdate,
  existingMode: Mode | undefined
): ArenaMode => {
  if (isArenaMode(partial.lastVisitedMode)) {
    return partial.lastVisitedMode;
  }
  if (isArenaMode(existingMode)) {
    return existingMode;
  }
  return Mode.ARENA_IN_SEASON;
};

export const mergeBoardViewUpdate = (
  existing: SportBoardView | undefined,
  partial: BoardViewUpdate
): SportBoardView => {
  const nextMode = partial.lastVisitedMode ?? existing?.lastVisitedMode;

  const modes: NonNullable<SportBoardView['modes']> = {
    ...existing?.modes,
  };

  if (partial.lastVisitedBoardTitle !== undefined) {
    modes.PRO = {
      ...modes.PRO,
      lastVisitedBoardTitle: partial.lastVisitedBoardTitle,
    };
  }

  if (
    partial.lastVisitedSo5Competition !== undefined ||
    partial.lastVisitedArenaRarity !== undefined
  ) {
    const arenaMode = resolveArenaWriteMode(partial, existing?.lastVisitedMode);
    modes[arenaMode] = {
      ...modes[arenaMode],
      ...(partial.lastVisitedSo5Competition !== undefined
        ? { lastVisitedSo5Competition: partial.lastVisitedSo5Competition }
        : {}),
      ...(partial.lastVisitedArenaRarity !== undefined
        ? { lastVisitedArenaRarity: partial.lastVisitedArenaRarity }
        : {}),
    };
  }

  return {
    lastVisitedMode: nextMode,
    modes: Object.keys(modes).length > 0 ? modes : undefined,
  };
};

export const getArenaModeState = (
  sportView: SportBoardView | undefined,
  mode: Mode
): ArenaModeState => {
  if (!isArenaMode(mode)) {
    return {};
  }
  return sportView?.modes?.[mode] ?? {};
};

export const getProModeState = (
  sportView: SportBoardView | undefined
): ProModeState => sportView?.modes?.PRO ?? {};
